import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Loader2, FileCheck } from 'lucide-react';
import ClientBasicInfoTab from './ClientBasicInfoTab';
import ClientTaxInfoTab from './ClientTaxInfoTab';
import ClientNotesTab from './ClientNotesTab';
import ClientHistoryTab from './ClientHistoryTab';
import ClientDocumentsTab from './ClientDocumentsTab';
import { MarkAsFiledDialog } from './MarkAsFiledDialog';
import { ClientBasicInfo, ClientTaxInfo } from '@/lib/clientValidation';

interface ClientDetailDrawerProps {
  clientId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function ClientDetailDrawer({
  clientId,
  open,
  onOpenChange,
}: ClientDetailDrawerProps) {
  const [clientData, setClientData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingBasic, setIsSavingBasic] = useState(false);
  const [isSavingTax, setIsSavingTax] = useState(false);
  const [isFiledDialogOpen, setIsFiledDialogOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (open && clientId) {
      fetchClientData();
    }
  }, [open, clientId]);

  const fetchClientData = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('id', clientId)
        .single();

      if (error) throw error;
      setClientData(data);
    } catch (error) {
      console.error('Error fetching client:', error);
      toast({
        title: 'Error',
        description: 'Failed to load client data',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const logClientUpdate = async (oldData: any, newData: any) => {
    try {
      const changes: Record<string, { old: any; new: any }> = {};
      
      Object.keys(newData).forEach(key => {
        if (JSON.stringify(oldData[key]) !== JSON.stringify(newData[key])) {
          changes[key] = {
            old: oldData[key],
            new: newData[key]
          };
        }
      });

      if (Object.keys(changes).length === 0) return;

      const { data: session } = await supabase.auth.getUser();

      await supabase.from('activity_log').insert({
        action: 'client_updated',
        client_id: clientId,
        user_id: session.user?.id,
        details: {
          changes,
          timestamp: new Date().toISOString()
        }
      });
    } catch (error) {
      console.error('Error logging activity:', error);
    }
  };

  const handleSaveBasicInfo = async (data: ClientBasicInfo) => {
    setIsSavingBasic(true);
    try {
      const oldData = { ...clientData };
      const updateData = {
        full_name: data.full_name,
        email: data.email,
        phone: data.phone || null,
        address: data.address || null,
        client_status: data.client_status,
        preferred_contact_method: data.preferred_contact_method || null,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('clients')
        .update(updateData)
        .eq('id', clientId);

      if (error) throw error;

      await logClientUpdate(oldData, { ...oldData, ...updateData });
      
      setClientData({ ...clientData, ...updateData });
      toast({
        title: 'Success',
        description: 'Basic information updated successfully',
      });
    } catch (error) {
      console.error('Error updating client:', error);
      toast({
        title: 'Error',
        description: 'Failed to update client information',
        variant: 'destructive',
      });
    } finally {
      setIsSavingBasic(false);
    }
  };

  const handleSaveTaxInfo = async (data: ClientTaxInfo) => {
    setIsSavingTax(true);
    try {
      const oldData = { ...clientData };
      const updateData = {
        tax_year: data.tax_year,
        filing_status: data.filing_status || null,
        ssn_last_4: data.ssn_last_4 || null,
        previous_year_agi: data.previous_year_agi || null,
        dependents: data.dependents || [],
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('clients')
        .update(updateData)
        .eq('id', clientId);

      if (error) throw error;

      await logClientUpdate(oldData, { ...oldData, ...updateData });
      
      setClientData({ ...clientData, ...updateData });
      toast({
        title: 'Success',
        description: 'Tax information updated successfully',
      });
    } catch (error) {
      console.error('Error updating client:', error);
      toast({
        title: 'Error',
        description: 'Failed to update tax information',
        variant: 'destructive',
      });
    } finally {
      setIsSavingTax(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <div className="flex items-start justify-between">
            <div>
              <SheetTitle>
                {isLoading ? 'Loading...' : `Client Details - ${clientData?.full_name}`}
              </SheetTitle>
              <SheetDescription>
                View and edit client information, notes, and history
              </SheetDescription>
            </div>
            {!isLoading && (
              <Button
                onClick={() => setIsFiledDialogOpen(true)}
                size="sm"
                className="ml-4"
              >
                <FileCheck className="h-4 w-4 mr-2" />
                Mark as Filed
              </Button>
            )}
          </div>
        </SheetHeader>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <Tabs defaultValue="basic-info" className="mt-6">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="basic-info">Basic Info</TabsTrigger>
              <TabsTrigger value="tax-info">Tax Info</TabsTrigger>
              <TabsTrigger value="notes">Notes</TabsTrigger>
              <TabsTrigger value="history">History</TabsTrigger>
              <TabsTrigger value="documents">Documents</TabsTrigger>
            </TabsList>

            <TabsContent value="basic-info" className="mt-6">
              <ClientBasicInfoTab
                defaultValues={{
                  full_name: clientData.full_name,
                  email: clientData.email,
                  phone: clientData.phone || '',
                  address: clientData.address || { street: '', city: '', state: '', zip: '' },
                  client_status: clientData.client_status,
                  preferred_contact_method: clientData.preferred_contact_method,
                }}
                onSave={handleSaveBasicInfo}
                isSaving={isSavingBasic}
              />
            </TabsContent>

            <TabsContent value="tax-info" className="mt-6">
              <ClientTaxInfoTab
                defaultValues={{
                  tax_year: clientData.tax_year,
                  filing_status: clientData.filing_status,
                  ssn_last_4: clientData.ssn_last_4,
                  previous_year_agi: clientData.previous_year_agi,
                  dependents: clientData.dependents || [],
                }}
                onSave={handleSaveTaxInfo}
                isSaving={isSavingTax}
              />
            </TabsContent>

            <TabsContent value="notes" className="mt-6">
              <ClientNotesTab clientId={clientId} />
            </TabsContent>

            <TabsContent value="history" className="mt-6">
              <ClientHistoryTab clientId={clientId} />
            </TabsContent>

            <TabsContent value="documents" className="mt-6">
              <ClientDocumentsTab clientId={clientId} />
            </TabsContent>
          </Tabs>
        )}

        {clientData && (
          <MarkAsFiledDialog
            open={isFiledDialogOpen}
            onOpenChange={setIsFiledDialogOpen}
            clientId={clientId}
            clientName={clientData.full_name}
            clientEmail={clientData.email}
            onComplete={fetchClientData}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}
